/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import { FREE_PLAN_ID } from "../../admin_store/utils/subscriptionPlan";
import { sanitizeStoreSlug } from "../../utils/sanitizeStoreSlug";
import { saveTenantPlan } from "./saveTenantPlan";

export const MODERN_THEME_ID = 3;
export const MODERN_THEME_VARIANT = "original";
export const TRIAL_WELCOME_KEY = "trialWelcomeToast";

const getBaseUrl = () =>
  (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

const pick = (...values) => values.find((v) => v != null && String(v).trim() !== "") || "";

async function fetchFreePlan(baseUrl) {
  const res = await fetch(`${baseUrl}/Plans/GetPlan?PlanId=-1`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);

  const data = await res.json();
  const list = Array.isArray(data) ? data : [];
  const free =
    list.find((p) => Number(p.planId || p.id) === FREE_PLAN_ID) ||
    list.find((p) => Number(p.price ?? p.Price ?? 1) === 0);

  if (!free) throw new Error("Free plan not found");

  const notes = [];
  for (let i = 1; i <= 8; i += 1) {
    notes.push(free[`note${i}`] || free[`Note${i}`] || "");
  }

  return {
    id: free.id || free.planId,
    planId: free.planId || free.id,
    title: free.planType || free.engPlanType || "Free",
    engTitle: free.engPlanType || "",
    desc: free.descripsion || free.description || "",
    price: Number(free.price || 0),
    notes,
  };
}

async function assertSlugAvailable(baseUrl, slug, currentTenantId) {
  try {
    const res = await fetch(`${baseUrl}/GetAllTenantFullDetails?tenantId=-1`);
    if (!res.ok) return;
    const allTenants = await res.json();
    if (!Array.isArray(allTenants)) return;
    const existing = allTenants.find((t) => {
      const otherId = t.TenantId ?? t.tenantId;
      const otherSlug = String(t.storeSlug || t.StoreSlug || "").toLowerCase();
      return Number(otherId) !== Number(currentTenantId) && otherSlug === slug;
    });
    if (existing) {
      const err = new Error("SLUG_TAKEN");
      err.code = "SLUG_TAKEN";
      throw err;
    }
  } catch (err) {
    if (err?.code === "SLUG_TAKEN") throw err;
  }
}

export async function completeStoreOnboarding({ storeName, storeSlug }) {
  const baseUrl = getBaseUrl();
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");
  const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
  const tenantDetails = JSON.parse(localStorage.getItem("tenantDetails") || "{}");
  const tenantId =
    localStorage.getItem("tenantId") ||
    tenant.tenantId ||
    tenant.TenantId ||
    tenantDetails.tenantId ||
    tenantDetails.TenantId;

  const finalSlug = sanitizeStoreSlug(storeSlug, { finalize: true });
  const finalStoreName = String(storeName || "").trim();

  if (!tenantId) throw new Error("TenantId missing");
  if (finalStoreName.length < 3) throw new Error("STORE_NAME_SHORT");
  if (finalSlug.length < 3) throw new Error("SLUG_INVALID");

  await assertSlugAvailable(baseUrl, finalSlug, tenantId);
  const plan = await fetchFreePlan(baseUrl);

  const personNameAr = pick(
    tenant.tenantName,
    tenant.TenantName,
    tenantDetails.tenantName,
    tenantDetails.TenantName,
    finalStoreName
  );
  const personNameEn = pick(
    tenant.engTenantName,
    tenant.EngTenantName,
    tenantDetails.engTenantName,
    tenantDetails.EngTenantName,
    personNameAr
  );

  const fd = new FormData();
  fd.append("TenantId", tenantId);
  fd.append("TenantName", personNameAr);
  fd.append("EngTenantName", personNameEn);
  fd.append("Email", pick(tenant.email, tenant.Email, tenantDetails.email, tenantDetails.Email));
  fd.append("Phone", pick(tenant.phone, tenant.Phone, tenantDetails.phone, tenantDetails.Phone));
  fd.append("SubDomain", pick(tenant.subDomain, tenant.SubDomain, tenantDetails.subDomain, tenantDetails.SubDomain));
  fd.append("IsActive", tenant.isActive !== undefined ? tenant.isActive : 1);
  fd.append(
    "CreatedAt",
    pick(tenant.createdAt, tenant.CreatedAt, tenantDetails.createdAt, tenantDetails.CreatedAt) ||
      new Date().toISOString()
  );
  fd.append("Product", pick(tenant.product, tenant.Product, tenantDetails.product, tenantDetails.Product));
  fd.append("TenantDtlId", tenantDetails.tenantDtlId || tenantDetails.TenantDtlId || 0);
  fd.append("ClientName", finalStoreName);
  fd.append("SectorType", "");
  fd.append("BusinessName", "");
  fd.append("CompanyName", personNameAr);
  fd.append("StoreSlug", finalSlug);
  fd.append("MainPlanId", plan.planId);
  fd.append("ThemeId", MODERN_THEME_ID);
  fd.append("DetailPlanId", plan.planId);
  fd.append("ColorName", MODERN_THEME_VARIANT);
  fd.append("PlanType", plan.title);
  fd.append("EngPlanType", plan.engTitle || "");
  fd.append("Price", plan.price);
  fd.append("Descripsion", plan.desc || "");
  fd.append("EngDescripsion", "");
  for (let i = 1; i <= 8; i += 1) {
    fd.append(`Note${i}`, plan.notes[i - 1] || "");
  }
  fd.append("PaymentMethods", JSON.stringify([]));
  fd.append("planId", plan.planId);

  const res = await fetch(`${baseUrl}/UpdateTenantDetails/UpdateTenantFullDetails`, {
    method: "POST",
    headers: { Authorization: token ? `Bearer ${token}` : "" },
    body: fd,
  });

  const updateData = await res.json().catch(() => ({}));
  if (!res.ok || updateData.success === false) {
    throw new Error(updateData.message || updateData.Message || "UpdateTenantDetails failed");
  }

  const mergedDetails = {
    ...tenantDetails,
    ...updateData,
    clientName: finalStoreName,
    ClientName: finalStoreName,
    storeSlug: finalSlug,
    StoreSlug: finalSlug,
    themeId: MODERN_THEME_ID,
    ThemeId: MODERN_THEME_ID,
    ColorName: MODERN_THEME_VARIANT,
    planId: plan.planId,
    PlanId: plan.planId,
    mainPlanId: plan.planId,
    MainPlanId: plan.planId,
  };

  localStorage.setItem("tenantDetails", JSON.stringify(mergedDetails));
  localStorage.setItem("companyStoreName", finalStoreName);
  localStorage.setItem("companyStoreSlug", finalSlug);
  localStorage.setItem("selectedThemeId", String(MODERN_THEME_ID));
  localStorage.setItem("selectedThemeVariant", MODERN_THEME_VARIANT);
  localStorage.setItem("selectedPlanId", String(plan.planId));
  localStorage.setItem("selectedPlan", plan.title);
  localStorage.setItem("selectedPlanType", plan.title || "");
  localStorage.setItem("selectedEngPlanType", plan.engTitle || "");
  localStorage.setItem("tenant", JSON.stringify({ ...tenant, storeSlug: finalSlug, StoreSlug: finalSlug }));

  for (let i = 1; i <= 8; i += 1) {
    localStorage.setItem(`selectedPlanNote${i}`, plan.notes[i - 1] || "");
  }

  await saveTenantPlan(plan, true);
  sessionStorage.setItem(TRIAL_WELCOME_KEY, "1");
}
